import { useCallback, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useSnackbar } from 'notistack';
import { downloadExportZip } from '../apiClient/downloadExportZip';
import {
  isExportJobActive,
  type ExportJob,
} from '../apiClient/fetchExportJobs';
import { useExportJobs, type WatchedExportEntry } from '../hooks/useExportJobs';

export default function ExportJobsStatusBar() {
  const { enqueueSnackbar } = useSnackbar();
  const {
    watchedEntries,
    dismissWatchedJob,
    invalidateExportJobs,
    openDownloadStatusModal,
    isSummaryFetching,
  } = useExportJobs();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const isOpen = watchedEntries.length > 0;

  const handleDownload = useCallback(
    async (job: ExportJob) => {
      if (!job.zip_filename) return;
      setDownloadingId(job.id);
      try {
        await downloadExportZip(job.zip_filename);
        invalidateExportJobs();
      } catch {
        enqueueSnackbar('Failed to download export file', {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        });
      } finally {
        setDownloadingId(null);
      }
    },
    [enqueueSnackbar, invalidateExportJobs],
  );

  return (
    <Snackbar
      open={isOpen}
      autoHideDuration={null}
      onClose={() => {}}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ bottom: { xs: 16, sm: 24 } }}
    >
      <Paper
        elevation={8}
        sx={{
          pointerEvents: 'auto',
          width: 'min(560px, calc(100vw - 32px))',
          maxHeight: 'min(40vh, 320px)',
          overflow: 'auto',
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'grey.50',
          }}
        >
          <Typography variant="subtitle2" component="p" sx={{ fontWeight: 600 }}>
            Export status
          </Typography>
          <Button size="small" onClick={openDownloadStatusModal}>
            View all
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {watchedEntries.map((entry) => (
            <WatchedExportRow
              key={entry.jobId}
              entry={entry}
              isFetching={isSummaryFetching}
              isDownloading={downloadingId === entry.jobId}
              onDownload={handleDownload}
              onDismiss={() => dismissWatchedJob(entry.jobId)}
            />
          ))}
        </Box>
      </Paper>
    </Snackbar>
  );
}

interface WatchedExportRowProps {
  entry: WatchedExportEntry;
  isFetching: boolean;
  isDownloading: boolean;
  onDownload: (job: ExportJob) => void;
  onDismiss: () => void;
}

function WatchedExportRow({
  entry,
  isFetching,
  isDownloading,
  onDownload,
  onDismiss,
}: WatchedExportRowProps) {
  const { job, jobId } = entry;
  const isActive = job ? isExportJobActive(job) : true;
  const isCompleted = job?.status === 'completed';
  const isFailed = job?.status === 'failed';
  const exportLabel = job?.export_type_display ?? 'Export';

  let statusLine: string;
  if (!job) {
    statusLine = 'Starting export…';
  } else if (job.status === 'pending') {
    statusLine = 'Queued…';
  } else if (job.status === 'processing') {
    statusLine = 'Preparing your file…';
  } else if (isCompleted) {
    statusLine = job.downloaded_at ? 'Downloaded' : 'Ready to download';
  } else if (isFailed) {
    statusLine = job.error_summary || 'Export failed';
  } else {
    statusLine = job.status;
  }

  const severity = isFailed ? 'error' : isCompleted ? 'success' : 'info';

  return (
    <Alert
      severity={severity}
      icon={
        isActive ? (
          <CircularProgress size={20} color="inherit" />
        ) : isCompleted ? (
          <CheckCircleOutlineIcon fontSize="inherit" />
        ) : isFailed ? (
          <ErrorOutlineIcon fontSize="inherit" />
        ) : undefined
      }
      sx={{
        borderRadius: 0,
        alignItems: 'flex-start',
        '& .MuiAlert-message': { width: '100%', py: 0.25 },
      }}
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {isCompleted && job?.zip_filename && !job.downloaded_at && (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              disabled={isDownloading}
              onClick={() => onDownload(job)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {isDownloading ? 'Downloading…' : 'Download'}
            </Button>
          )}
          {!isActive && (
            <IconButton
              size="small"
              color="inherit"
              aria-label="Dismiss export status"
              onClick={onDismiss}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      }
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {exportLabel}
        {job && (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
            {' '}
            · {job.collection_count} collection{job.collection_count === 1 ? '' : 's'}
          </Typography>
        )}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {statusLine}
        {isActive && isFetching && !job && ' (syncing)'}
      </Typography>
      {!job && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
          Job {jobId.slice(0, 8)}…
        </Typography>
      )}
    </Alert>
  );
}
